import React from 'react';
import { ErrorStats } from '@/hooks/useAdminErrors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, AlertTriangle, TrendingUp, XCircle } from 'lucide-react';

interface ErrorStatsChartsProps {
  stats: ErrorStats;
}

export const ErrorStatsCharts: React.FC<ErrorStatsChartsProps> = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Total Errors */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Errors</CardTitle>
          <AlertCircle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.total_errors.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">All time</p>
        </CardContent>
      </Card>

      {/* Unresolved Errors */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Unresolved</CardTitle>
          <AlertTriangle className="h-4 w-4 text-orange-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.unresolved_errors.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {stats.total_errors > 0
              ? `${((stats.unresolved_errors / stats.total_errors) * 100).toFixed(1)}% of total`
              : 'No errors'}
          </p>
        </CardContent>
      </Card>

      {/* Critical Errors */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Critical</CardTitle>
          <XCircle className="h-4 w-4 text-red-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.critical_errors.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Require immediate attention</p>
        </CardContent>
      </Card>

      {/* Last 24h */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Last 24 Hours</CardTitle>
          <TrendingUp className="h-4 w-4 text-blue-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.errors_last_24h.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {stats.errors_last_7d > 0
              ? `${((stats.errors_last_24h / stats.errors_last_7d) * 100).toFixed(1)}% of weekly`
              : 'Recent activity'}
          </p>
        </CardContent>
      </Card>

      {/* By Type */}
      <Card className="col-span-full md:col-span-1 lg:col-span-2">
        <CardHeader>
          <CardTitle>By Type</CardTitle>
          <CardDescription>Distribution of error types</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.entries(stats.by_type).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{type}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium">{count}</div>
                  <div className="w-32 bg-muted rounded-full h-2">
                    <div
                      className="bg-primary rounded-full h-2 transition-all"
                      style={{
                        width: `${(count / stats.total_errors) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* By Severity */}
      <Card className="col-span-full md:col-span-1 lg:col-span-2">
        <CardHeader>
          <CardTitle>By Severity</CardTitle>
          <CardDescription>Distribution of error severity levels</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.entries(stats.by_severity).map(([severity, count]) => {
              const colors: Record<string, string> = {
                critical: 'bg-red-500',
                error: 'bg-orange-500',
                warning: 'bg-yellow-500',
                info: 'bg-blue-500',
              };
              return (
                <div key={severity} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        severity === 'critical' || severity === 'error'
                          ? 'destructive'
                          : 'default'
                      }
                    >
                      {severity}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">{count}</div>
                    <div className="w-32 bg-muted rounded-full h-2">
                      <div
                        className={`${colors[severity]} rounded-full h-2 transition-all`}
                        style={{
                          width: `${(count / stats.total_errors) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Top Errors */}
      {stats.top_errors.length > 0 && (
        <Card className="col-span-full">
          <CardHeader>
            <CardTitle>Top Unresolved Errors</CardTitle>
            <CardDescription>Most frequent errors requiring attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.top_errors.slice(0, 5).map((error) => (
                <div
                  key={error.id}
                  className="flex items-start justify-between p-3 bg-muted rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm truncate">{error.message}</div>
                    {error.error_code && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {error.error_code}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Badge variant="outline">{error.occurrence_count} occurrences</Badge>
                    <Badge
                      variant={
                        error.severity === 'critical' || error.severity === 'error'
                          ? 'destructive'
                          : 'default'
                      }
                    >
                      {error.severity}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
